import { Box, Button, SxProps, Typography } from '@mui/material';
import { grayText } from 'app/components/ThemeProvider/colors';
import { useCustomNavigate } from 'app/utils/customHooks/useCustomNavigate';
import { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

const container: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: '16px',
  padding: '48px 24px',
};

// Only for standalone page usage — a Dialog already supplies its own paper
// background/shadow, so EmptyStateDialog opts out via `card={false}` to
// avoid stacking two sets of card chrome.
const cardChrome: SxProps = {
  background: 'white',
  borderRadius: '8px',
  boxShadow: '0px 0px 32px 0px #0000000F',
};

const illustrationBox: SxProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const title: SxProps = {
  fontSize: '20px',
  fontWeight: 600,
};

const description: SxProps = {
  fontSize: '15px',
  color: grayText,
};

const defaultIllustration = (
  <img width={185} height={157} src='/images/guest_login_icon.png' alt='' />
);

interface EmptyStateAction {
  label?: string;
  onClick?: () => void;
}

export interface EmptyStateProps {
  illustration?: ReactNode;
  title?: string;
  description?: ReactNode;
  action?: EmptyStateAction;
  /** Set to false when a parent already provides card chrome (e.g. a Dialog). Defaults to true. */
  card?: boolean;
}

const EmptyState = (props: EmptyStateProps) => {
  const { t } = useTranslation();
  const navigate = useCustomNavigate();
  const card = props.card ?? true;

  return (
    <Box sx={card ? { ...container, ...cardChrome } : container}>
      <Box sx={illustrationBox}>{props.illustration ?? defaultIllustration}</Box>
      <Typography sx={title}>{props.title ?? t('common.noAccountTitle')}</Typography>
      <Typography sx={description}>
        {props.description ?? (
          <Trans
            i18nKey='common.noAccountDescription'
            components={{ bold: <Typography component='span' fontWeight={600} /> }}
          />
        )}
      </Typography>
      {props.action && (
        <Button onClick={props.action?.onClick ?? (() => navigate('login'))} sx={{ mt: '8px' }}>
          {props.action?.label ?? t('login.goToRegister')}
        </Button>
      )}
    </Box>
  );
};

export default EmptyState;
